/**
 * Adapted from PlayCanvas Engine's gsplat-world-state.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import type * as THREE from 'three'
import {
  GSplatPickIndex,
  type GSplatPickWorkBufferSource,
} from '@/plugins/gsplat/unified/GSplatPickIndex'
import { type BlockAllocator, MemBlock } from '@/plugins/gsplat/unified/world/BlockAllocator'
import type { Disposable } from '@/shared/types/core'

export interface GSplatWorldResource {
  readonly id: number
  readonly centersVersion: number
  readonly centers: Float32Array | null
  readSplatShape?(
    index: number,
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    radii: THREE.Vector3
  ): boolean
  readSplatColor?(index: number, color: THREE.Vector4): boolean
}

export interface GSplatWorldSplat extends Partial<Disposable> {
  readonly allocId: number
  readonly intervalAllocIds: readonly number[]
  readonly intervals: readonly number[]
  readonly intervalNodeIndices?: readonly number[]
  readonly activeSplats: number
  readonly resource: GSplatWorldResource
  readonly assetId?: string
  readonly placementId?: number
  readonly isEnvironment?: boolean
  readonly node: THREE.Object3D
  readonly aabb: THREE.Box3
  readonly parentPlacementId?: number
  readonly numBoundsEntries?: number
  boundsBaseIndex?: number
  setLayout(intervalOffsets: readonly number[]): void
}

export interface GSplatWorldBoundsGroup {
  readonly splat: GSplatWorldSplat
  boundsBaseIndex: number
  readonly numBoundsEntries: number
}

/** Persistent allocator 生成的 versioned render-ready layout snapshot。 */
export class GSplatWorldState implements Disposable {
  readonly version: number
  /** 与当前 View/Manager layout 同代，不读取其它 View 的 active set。 */
  readonly pickIndex: GSplatPickIndex
  readonly splats: GSplatWorldSplat[]
  readonly needsUpload: GSplatWorldSplat[] = []
  readonly needsUploadIds = new Set<number>()
  readonly allocIdToSplat = new Map<number, GSplatWorldSplat>()
  readonly intervalOffsetsBySplat = new Map<GSplatWorldSplat, readonly number[]>()
  readonly boundsGroups: GSplatWorldBoundsGroup[] = []
  textureSize = 1
  totalActiveSplats = 0
  totalIntervals = 0
  fullRebuild = false
  sortParametersSet = false
  sortedBefore = false

  private destroyed = false

  constructor(
    version: number,
    splats: readonly GSplatWorldSplat[],
    allocator: BlockAllocator,
    allocationMap: Map<number, MemBlock>,
    maxTextureDimension2D: number,
    pickIndex: GSplatPickIndex = GSplatPickIndex.empty()
  ) {
    this.version = version
    this.splats = [...splats]
    if (splats.length === 0) {
      for (const block of allocationMap.values()) allocator.free(block)
      allocationMap.clear()
      this.pickIndex = pickIndex
      return
    }

    const desired = this.collectDesiredAllocations(splats)
    const toFree: MemBlock[] = []
    const toAllocateIds: number[] = []
    const toAllocate: Array<number | MemBlock> = []

    for (const [allocId, block] of allocationMap) {
      const next = desired.get(allocId)
      if (!next || next.size !== block.size) {
        toFree.push(block)
        allocationMap.delete(allocId)
      }
    }
    for (const [allocId, entry] of desired) {
      if (!allocationMap.has(allocId) && entry.size > 0) {
        toAllocateIds.push(allocId)
        toAllocate.push(entry.size)
      }
    }

    this.fullRebuild = allocator.updateAllocation(toFree, toAllocate)
    for (let index = 0; index < toAllocateIds.length; index++) {
      const block = toAllocate[index]
      if (!(block instanceof MemBlock)) throw new Error('GSplat allocation 未发布 MemBlock')
      allocationMap.set(toAllocateIds[index], block)
    }

    const changedAllocIds = new Set(toAllocateIds)
    if (!this.fullRebuild && allocator.fragmentation > 0.3) {
      const moved = allocator.defrag(Math.max(50, toFree.length + toAllocateIds.length))
      for (const [allocId, block] of allocationMap) {
        if (moved.has(block)) changedAllocIds.add(allocId)
      }
    }

    this.textureSize = allocator.capacity > 0 ? Math.ceil(Math.sqrt(allocator.capacity)) : 1
    if (this.textureSize > maxTextureDimension2D) {
      throw new Error(
        `GSplat WorldState texture 超限：actual=${this.textureSize}, limit=${maxTextureDimension2D}`
      )
    }
    this.assignLayouts(splats, allocationMap, changedAllocIds)
    this.buildBoundsGroups(splats)
    this.pickIndex =
      pickIndex.size > 0
        ? pickIndex
        : GSplatPickIndex.fromWorkBufferSources(this.buildPickSources(splats))
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    for (const splat of this.splats) splat.dispose?.()
    this.splats.length = 0
    this.needsUpload.length = 0
    this.needsUploadIds.clear()
    this.allocIdToSplat.clear()
    this.intervalOffsetsBySplat.clear()
    this.boundsGroups.length = 0
  }

  dispose(): void {
    this.destroy()
  }

  private collectDesiredAllocations(
    splats: readonly GSplatWorldSplat[]
  ): Map<number, { size: number; splat: GSplatWorldSplat }> {
    const desired = new Map<number, { size: number; splat: GSplatWorldSplat }>()
    for (const splat of splats) {
      if (!Number.isSafeInteger(splat.activeSplats) || splat.activeSplats < 0) {
        throw new Error(`非法 GSplat activeSplats: ${splat.activeSplats}`)
      }
      if (splat.intervals.length % 2 !== 0) throw new Error('GSplat intervals 必须为 start/end 对')
      const intervalCount = splat.intervals.length / 2
      if (intervalCount > 0 && splat.intervalAllocIds.length === intervalCount) {
        for (let index = 0; index < intervalCount; index++) {
          const start = splat.intervals[index * 2]
          const end = splat.intervals[index * 2 + 1]
          this.addDesired(desired, splat.intervalAllocIds[index], end - start, splat)
        }
      } else {
        this.addDesired(desired, splat.allocId, splat.activeSplats, splat)
      }
    }
    return desired
  }

  private addDesired(
    desired: Map<number, { size: number; splat: GSplatWorldSplat }>,
    allocId: number,
    size: number,
    splat: GSplatWorldSplat
  ): void {
    if (!Number.isSafeInteger(allocId) || allocId < 0) throw new Error(`非法 allocId: ${allocId}`)
    if (!Number.isSafeInteger(size) || size < 0) throw new Error(`非法 allocation size: ${size}`)
    if (desired.has(allocId)) throw new Error(`GSplat allocId 重复: ${allocId}`)
    desired.set(allocId, { size, splat })
  }

  private assignLayouts(
    splats: readonly GSplatWorldSplat[],
    allocationMap: ReadonlyMap<number, MemBlock>,
    changedAllocIds: ReadonlySet<number>
  ): void {
    for (const splat of splats) {
      const intervalOffsets: number[] = []
      let changed = this.fullRebuild
      const intervalCount = splat.intervals.length / 2
      this.totalIntervals += intervalCount > 0 ? intervalCount : 1

      if (intervalCount > 0 && splat.intervalAllocIds.length === intervalCount) {
        for (let index = 0; index < intervalCount; index++) {
          const allocId = splat.intervalAllocIds[index]
          this.allocIdToSplat.set(allocId, splat)
          const block = allocationMap.get(allocId)
          if (!block) continue
          intervalOffsets.push(block.offset)
          this.totalActiveSplats += splat.intervals[index * 2 + 1] - splat.intervals[index * 2]
          if (changedAllocIds.has(allocId)) {
            changed = true
            this.needsUploadIds.add(allocId)
          }
          if (this.fullRebuild) this.needsUploadIds.add(allocId)
        }
      } else {
        this.allocIdToSplat.set(splat.allocId, splat)
        const block = allocationMap.get(splat.allocId)
        if (block) {
          intervalOffsets.push(block.offset)
          this.totalActiveSplats += splat.activeSplats
          if (changedAllocIds.has(splat.allocId)) {
            changed = true
            this.needsUploadIds.add(splat.allocId)
          }
          if (this.fullRebuild) this.needsUploadIds.add(splat.allocId)
        }
      }
      if (intervalOffsets.length > 0) {
        const frozenOffsets = Object.freeze(intervalOffsets)
        this.intervalOffsetsBySplat.set(splat, frozenOffsets)
        splat.setLayout(frozenOffsets)
      }
      if (changed) this.needsUpload.push(splat)
    }
  }

  private buildBoundsGroups(splats: readonly GSplatWorldSplat[]): void {
    const groups = new Map<number, GSplatWorldBoundsGroup>()
    for (const splat of splats) {
      const key = splat.parentPlacementId ?? splat.allocId
      if (!groups.has(key)) {
        groups.set(key, {
          splat,
          boundsBaseIndex: 0,
          numBoundsEntries: splat.numBoundsEntries ?? 1,
        })
      }
    }

    let boundsBaseIndex = 0
    for (const group of groups.values()) {
      group.boundsBaseIndex = boundsBaseIndex
      boundsBaseIndex += group.numBoundsEntries
      this.boundsGroups.push(group)
    }
    for (const splat of splats) {
      const group = groups.get(splat.parentPlacementId ?? splat.allocId)!
      splat.boundsBaseIndex = group.boundsBaseIndex
    }
  }

  private buildPickSources(splats: readonly GSplatWorldSplat[]): GSplatPickWorkBufferSource[] {
    return splats.map((splat, assetOrder) => ({
      assetId: splat.assetId,
      placementId: splat.placementId ?? splat.allocId,
      sourceResourceId: splat.resource.id,
      activeSplats: splat.activeSplats,
      intervals: splat.intervals,
      intervalOffsets: this.intervalOffsetsBySplat.get(splat),
      assetOrder,
    }))
  }
}
